import { useState, useEffect, Fragment } from "react";
import "./custom-styles.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Edit,
  Trash2,
  PlusCircle,
  Calendar,
  Eye,
  MoreHorizontal,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import api from "@/services/api";

// Types
interface Consumer {
  id: string;
  fullName: string;
  registrationNumber: string;
  contactNumber: string;
  categoryId: string;
  divisionId: string;
  zoneId: string;
  category?: { id: string; name: string };
  division?: { id: string; name: string };
  zone?: { id: string; name: string };
}

interface Service {
  id: string;
  name: string;
  code: string;
  amount: number;
  status: boolean;
}

interface CustomerService {
  serviceName: string;
  paidAmount: number;
  dueAmount: number;
}

interface CollectionConfig {
  id: string;
  entryNo: string;
  name: string;
  category: string;
  service: string;
  amount: string;
  billingCycle: string;
  period: string;
  status: string;
}

// No mocks; services and collections fetched from API

const getBillingDateOptions = (billingCycle: string) => {
  switch (billingCycle) {
    case "daily":
      return null;
    case "weekly":
      return Array.from({ length: 7 }, (_, i) => ({
        value: (i + 1).toString(),
        label: `Every ${
          [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ][i]
        }`,
      }));
    case "monthly":
      return Array.from({ length: 31 }, (_, i) => ({
        value: (i + 1).toString(),
        label: `${i + 1}${getOrdinalSuffix(i + 1)} of month`,
      }));
    case "quarterly":
      return [
        { value: "1", label: "January (Q1)" },
        { value: "2", label: "April (Q2)" },
        { value: "3", label: "July (Q3)" },
        { value: "4", label: "October (Q4)" },
      ];
    case "yearly":
      return [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ].map((month, index) => ({
        value: (index + 1).toString(),
        label: month,
      }));
    case "one_time":
      return null;
    default:
      return null;
  }
};

const getOrdinalSuffix = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Format billing date based on billing cycle
const formatBillingDate = (
  billingCycle: string,
  billingDate: string | number
) => {
  if (!billingDate) return "--";

  const dateNum = Number(billingDate);

  switch (billingCycle) {
    case "weekly":
      const weekdays = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      return weekdays[dateNum - 1] || "--";

    case "monthly":
      return `${dateNum}${getOrdinalSuffix(dateNum)} of month`;

    case "quarterly":
      const quarters = [
        "January (Q1)",
        "April (Q2)",
        "July (Q3)",
        "October (Q4)",
      ];
      return quarters[dateNum - 1] || "--";

    case "yearly":
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return months[dateNum - 1] || "--";

    default:
      return "--";
  }
};

const getDefaultDueDays = (billingCycle: string) => {
  switch (billingCycle) {
    case "daily":
      return 1;
    case "weekly":
      return 7;
    case "monthly":
      return 15;
    case "quarterly":
      return 30;
    case "yearly":
      return 30;
    case "one_time":
      return 15;
    default:
      return 15;
  }
};

const CollectionManagement = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("consumers");
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [collections, setCollections] = useState<CollectionConfig[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  // Consumer section state
  const [selectedConsumers, setSelectedConsumers] = useState<string[]>([]);
  const [selectAllConsumers, setSelectAllConsumers] = useState(false);
  const [consumerFilters, setConsumerFilters] = useState({
    categoryId: "all",
    divisionId: "all",
    zoneId: "all",
  });
  const [consumerSearchTerm, setConsumerSearchTerm] = useState("");
  const [masterData, setMasterData] = useState({
    categories: [] as Array<{ id: string; name: string }>,
    divisions: [] as Array<{ id: string; name: string }>,
    zones: [] as Array<{ id: string; name: string }>,
  });

  // Collection form state
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(
    null
  );
  const [collectionFormData, setCollectionFormData] = useState({
    entryNo: "",
    name: "",
    serviceId: "",
    service: "",
    amount: "",
    billingCycle: "",
    billingDate: "",
    dueDays: 0,
    startDate: "",
    endDate: "",
    status: "active",
  });

  // Collection section state
  const [collectionFilters, setCollectionFilters] = useState({
    serviceId: "all",
    status: "all",
  });
  const [collectionSearchTerm, setCollectionSearchTerm] = useState("");
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [selectedCollectionDetails, setSelectedCollectionDetails] =
    useState<CollectionConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<string | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [entryNoLoading, setEntryNoLoading] = useState(false);
  const [expandedConsumerId, setExpandedConsumerId] = useState<string | null>(
    null
  );
  const [customerServices, setCustomerServices] = useState<CustomerService[]>(
    []
  );
  const [serviceLoading, setServiceLoading] = useState(false);
  const [selectedConsumerName, setSelectedConsumerName] = useState("");

  // View/Edit Collection Dialog State
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<any>(null);
  const [editConsumers, setEditConsumers] = useState<any[]>([]);
  const [selectedEditConsumers, setSelectedEditConsumers] = useState<string[]>(
    []
  );
  const [selectAllEditConsumers, setSelectAllEditConsumers] = useState(false);
  const [collectionLoading, setCollectionLoading] = useState(false);

  // Server returns filtered list; no client filtering needed
  const filteredConsumers = consumers;

  // Fetch master data for dropdowns
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [categoriesRes, divisionsRes, zonesRes] = await Promise.all([
          api.category.getCategories({ status: "true" }),
          api.division.getDivisions({ status: "true" }),
          api.zone.getZones({ status: "true" }),
        ]);
        setMasterData({
          categories: categoriesRes.docs || [],
          divisions: divisionsRes.docs || [],
          zones: zonesRes.docs || [],
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to load filters",
          variant: "destructive",
        });
      }
    };
    fetchMasters();
  }, []);

  // Fetch consumers when filters/search change
  useEffect(() => {
    const fetchConsumers = async () => {
      try {
        const res = await api.consumer.getConsumers({
          // casting to any to allow limit as "all" as per requirement
          limit: "all" as any,
          query: consumerSearchTerm.trim() || undefined,
          categoryId:
            consumerFilters.categoryId !== "all"
              ? consumerFilters.categoryId
              : undefined,
          divisionId:
            consumerFilters.divisionId !== "all"
              ? consumerFilters.divisionId
              : undefined,
          zoneId:
            consumerFilters.zoneId !== "all"
              ? consumerFilters.zoneId
              : undefined,
          status: "active",
        } as any);
        setConsumers(res.docs || []);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to load consumers",
          variant: "destructive",
        });
      }
    };
    fetchConsumers();
  }, [consumerSearchTerm, consumerFilters]);

  // Fetch services for Service filter
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.service.getServices({ status: "true" });
        setServices(res.docs || []);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to load services",
          variant: "destructive",
        });
      }
    };
    fetchServices();
  }, []);

  // Fetch collections from API using query, serviceId, and status
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        const res = await api.collection.getCollections({
          // casting to any to allow limit as "all"
          limit: "all" as any,
          query: collectionSearchTerm.trim() || undefined,
          serviceId:
            collectionFilters.serviceId !== "all"
              ? collectionFilters.serviceId
              : undefined,
          status:
            collectionFilters.status !== "all"
              ? collectionFilters.status
              : undefined,
        } as any);

        const mapped = (res.docs || []).map(
          (cfg: any) =>
            ({
              id: cfg.id,
              entryNo: (cfg.entryNumber ?? "").toString().trim(),
              name: (cfg.name ?? "").toString().trim(),
              category: cfg.category?.name ?? "",
              service: cfg.service?.name ?? "",
              amount: (() => {
                const n = Number(cfg.amount ?? 0);
                return `₹${n.toFixed(2)}`;
              })(),
              billingCycle: (cfg.billingCycle ?? "").toString(),
              period:
                cfg.startDate && cfg.endDate
                  ? `${format(
                      new Date(cfg.startDate),
                      "MMM d, yyyy"
                    )} - ${format(new Date(cfg.endDate), "MMM d, yyyy")}`
                  : "--",
              status: (cfg.status ?? "").toString(),
            } as CollectionConfig)
        );
        setCollections(mapped);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to load collections",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, [collectionSearchTerm, collectionFilters]);

  const toggleConsumer = (consumerId: string) => {
    setSelectedConsumers((prev) => {
      if (prev.includes(consumerId)) {
        const newSelected = prev.filter((id) => id !== consumerId);
        if (newSelected.length !== filteredConsumers.length) {
          setSelectAllConsumers(false);
        }
        return newSelected;
      } else {
        const newSelected = [...prev, consumerId];
        if (newSelected.length === filteredConsumers.length) {
          setSelectAllConsumers(true);
        }
        return newSelected;
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectAllConsumers) {
      setSelectedConsumers([]);
      setSelectAllConsumers(false);
    } else {
      setSelectedConsumers(filteredConsumers.map((c) => c.id));
      setSelectAllConsumers(true);
    }
  };

  const handleCreateCollection = async () => {
    if (selectedConsumers.length === 0) {
      toast({
        title: "No Consumers Selected",
        description:
          "Please select at least one consumer to create a collection.",
        variant: "destructive",
      });
      return;
    }
    setIsEditMode(false);
    setEditingCollectionId(null);
    setEntryNoLoading(true);
    try {
      const res = await api.collection.getEntryNumber();
      console.log("entry number", res);
      const nextEntryNo = (res.data ?? "").toString();
      setCollectionFormData({
        entryNo: nextEntryNo,
        name: "",
        serviceId: "",
        service: "",
        amount: "",
        billingCycle: "",
        billingDate: "",
        dueDays: 0,
        startDate: "",
        endDate: "",
        status: "active",
      });
      setShowCollectionForm(true);
    } catch (error: any) {
      toast({
        title: "Failed to fetch entry number",
        description: error.message ?? "Could not load entry number from server",
        variant: "destructive",
      });
    } finally {
      setEntryNoLoading(false);
    }
  };

  // Fetch collections function
  const fetchCollections = async () => {
    try {
      const response = await api.collection.getCollections();
      setCollections(response.docs || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to load collections",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCollection = (collectionId: string) => {
    setCollectionToDelete(collectionId);
    setDeleteConfirmOpen(true);
  };

  const handleViewCustomerServices = async (consumer: Consumer) => {
    // If clicking on the same row, toggle it closed
    if (expandedConsumerId === consumer.id) {
      setExpandedConsumerId(null);
      return;
    }

    setServiceLoading(true);
    setSelectedConsumerName(consumer.fullName);
    setExpandedConsumerId(consumer.id);

    try {
      const response = await api.service.getCustomerServices(consumer.id);
      if (response.data && Array.isArray(response.data)) {
        setCustomerServices(response.data);
      } else {
        setCustomerServices([]);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to load customer services",
        variant: "destructive",
      });
      setExpandedConsumerId(null);
    } finally {
      setServiceLoading(false);
    }
  };

  const confirmDeleteCollection = async () => {
    if (collectionToDelete) {
      setLoading(true);
      try {
        // Call the API to delete the collection
        await api.collection.deleteCollection(collectionToDelete);

        // Update the UI by removing the deleted collection
        setCollections((prev) =>
          prev.filter((c) => c.id !== collectionToDelete)
        );

        // Show success toast notification
        toast({
          title: "Collection Deleted",
          description: "The collection has been deleted successfully.",
        });
      } catch (error: any) {
        // Show error toast notification
        toast({
          title: "Error",
          description: error.message ?? "Failed to delete collection",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setDeleteConfirmOpen(false);
        setCollectionToDelete(null);
      }
    }
  };

  const handleCollectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode && editingCollectionId) {
        // Update existing collection
        setCollections((prev) =>
          prev.map((c) =>
            c.id === editingCollectionId
              ? {
                  ...c,
                  name: collectionFormData.name,
                  service: collectionFormData.service,
                  amount: `₹${parseFloat(collectionFormData.amount).toFixed(
                    2
                  )}`,
                  billingCycle:
                    collectionFormData.billingCycle.charAt(0).toUpperCase() +
                    collectionFormData.billingCycle.slice(1),
                  status: collectionFormData.status,
                }
              : c
          )
        );
        toast({
          title: "Collection Updated",
          description: `${collectionFormData.name} has been updated successfully.`,
        });
      } else {
        // Create new collection via API
        await api.collection.createCollection({
          name: collectionFormData.name,
          // optional IDs intentionally omitted: categoryId, divisionId, zoneId
          serviceId: collectionFormData.serviceId,
          amount: Number(collectionFormData.amount),
          billingCycle: collectionFormData.billingCycle,
          startDate: collectionFormData.startDate,
          endDate: collectionFormData.endDate,
          dueDays: Number(collectionFormData.dueDays),
          status: collectionFormData.status,
          consumerIds: selectedConsumers,
        } as any);

        // Refresh collection list from server
        try {
          const res = await api.collection.getCollections({
            limit: "all" as any,
            query: collectionSearchTerm.trim() || undefined,
            serviceId:
              collectionFilters.serviceId !== "all"
                ? collectionFilters.serviceId
                : undefined,
            status:
              collectionFilters.status !== "all"
                ? collectionFilters.status
                : undefined,
          } as any);
          const mapped = (res.docs || []).map(
            (cfg: any) =>
              ({
                id: cfg.id,
                entryNo: (cfg.entryNumber ?? "").toString().trim(),
                name: (cfg.name ?? "").toString().trim(),
                category: cfg.category?.name ?? "",
                service: cfg.service?.name ?? "",
                amount: (() => {
                  const n = Number(cfg.amount ?? 0);
                  return `₹${n.toFixed(2)}`;
                })(),
                billingCycle: (cfg.billingCycle ?? "").toString(),
                period:
                  cfg.startDate && cfg.endDate
                    ? `${format(
                        new Date(cfg.startDate),
                        "MMM d, yyyy"
                      )} - ${format(new Date(cfg.endDate), "MMM d, yyyy")}`
                    : "--",
                status: (cfg.status ?? "").toString(),
              } as CollectionConfig)
          );
          setCollections(mapped);
        } catch {}

        toast({
          title: "Collection Created",
          description: `${collectionFormData.name} has been created successfully.`,
        });
      }
      setShowCollectionForm(false);
      setSelectedConsumers([]);
      setSelectAllConsumers(false);
    } catch (error) {
      toast({
        title: "Error",
        description: (error as any)?.message ?? "Failed to save collection",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    const service = services.find((s) => s.id === serviceId);
    setCollectionFormData((prev) => ({
      ...prev,
      serviceId,
      service: service?.name || "",
      amount: service?.amount.toString() || "",
    }));
  };

  useEffect(() => {
    if (collectionFormData.billingCycle) {
      const options = getBillingDateOptions(collectionFormData.billingCycle);
      if (options && options.length > 0) {
        setCollectionFormData((prev) => ({
          ...prev,
          billingDate: options[0].value,
        }));
      } else {
        setCollectionFormData((prev) => ({ ...prev, billingDate: "" }));
      }
      setCollectionFormData((prev) => ({
        ...prev,
        dueDays: getDefaultDueDays(collectionFormData.billingCycle),
      }));
    }
  }, [collectionFormData.billingCycle]);

  // View Collection Handler
  const handleViewCollection = async (collection: CollectionConfig) => {
    setCollectionLoading(true);
    try {
      const response = await api.collection.getCollection(collection.id);
      setSelectedCollection(response.data);
      setShowViewDialog(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to load collection details",
        variant: "destructive",
      });
    } finally {
      setCollectionLoading(false);
    }
  };

  // Edit Collection Handler
  const handleEditCollection = async (collection: CollectionConfig) => {
    setCollectionLoading(true);
    try {
      const response = await api.collection.getCollection(collection.id);
      const collectionData = response.data;
      setSelectedCollection(collectionData);
      setEditConsumers(collectionData.consumers || []);
      setSelectedEditConsumers(
        collectionData.consumers?.map((c: any) => c.id) || []
      );
      setSelectAllEditConsumers(collectionData.consumers?.length > 0);
      setShowEditDialog(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to load collection details",
        variant: "destructive",
      });
    } finally {
      setCollectionLoading(false);
    }
  };

  // Toggle consumer selection in edit dialog
  const toggleEditConsumer = (consumerId: string) => {
    setSelectedEditConsumers((prev) => {
      const newSelected = prev.includes(consumerId)
        ? prev.filter((id) => id !== consumerId)
        : [...prev, consumerId];

      setSelectAllEditConsumers(newSelected.length === editConsumers.length);
      return newSelected;
    });
  };

  // Toggle select all consumers in edit dialog
  const toggleSelectAllEditConsumers = () => {
    if (selectAllEditConsumers) {
      setSelectedEditConsumers([]);
      setSelectAllEditConsumers(false);
    } else {
      setSelectedEditConsumers(editConsumers.map((c) => c.id));
      setSelectAllEditConsumers(true);
    }
  };

  // Save edited collection
  const handleSaveEditCollection = async () => {
    if (selectedEditConsumers.length === 0) {
      toast({
        title: "No Consumers Selected",
        description: "Please select at least one consumer.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Create a clean payload with only the fields needed for the API
      const payload = {
        id: selectedCollection.id,
        name: selectedCollection.name,
        serviceId: selectedCollection.serviceId,
        amount: selectedCollection.amount,
        billingCycle: selectedCollection.billingCycle,
        status: selectedCollection.status,
        consumerIds: selectedEditConsumers,
        billingDate: selectedCollection.billingDate,
      };

      // Add billing date if applicable based on billing cycle
      if (
        selectedCollection.billingCycle &&
        selectedCollection.billingCycle !== "daily" &&
        selectedCollection.billingCycle !== "one_time"
      ) {
        payload.billingDate = selectedCollection.billingDate;
      }

      await api.collection.updateCollection(selectedCollection.id, payload);

      toast({
        title: "Collection Updated",
        description: "Collection has been updated successfully.",
      });

      setShowEditDialog(false);
      // Refresh collections list
      fetchCollections();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to update collection",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Collection Management</h1>
          <p className="text-muted-foreground">
            Record payments and manage collection configurations
          </p>
        </div>
        {activeTab === "consumers" && (
          <Button
            onClick={handleCreateCollection}
            disabled={selectedConsumers.length === 0}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Collection
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1">
        <Button
          variant={activeTab === "consumers" ? "default" : "outline"}
          onClick={() => setActiveTab("consumers")}
          className={
            activeTab === "consumers" ? "bg-purple-600 hover:bg-purple-700" : ""
          }
        >
          Consumer List
        </Button>
        <Button
          variant={activeTab === "collections" ? "default" : "outline"}
          onClick={() => setActiveTab("collections")}
          className={
            activeTab === "collections"
              ? "bg-purple-600 hover:bg-purple-700"
              : ""
          }
        >
          Collection List
        </Button>
      </div>

      {/* Consumer List Tab */}
      {activeTab === "consumers" && (
        <Card>
          <CardContent className="pt-6">
            {/* Consumer Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="space-y-2">
                <Label
                  htmlFor="consumer-search"
                  className="flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Search
                </Label>
                <Input
                  id="consumer-search"
                  placeholder="Search Consumer..."
                  value={consumerSearchTerm}
                  onChange={(e) => setConsumerSearchTerm(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Category
                </Label>
                <Select
                  value={consumerFilters.categoryId}
                  onValueChange={(value) =>
                    setConsumerFilters((prev) => ({
                      ...prev,
                      categoryId: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {masterData.categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Division</Label>
                <Select
                  value={consumerFilters.divisionId}
                  onValueChange={(value) =>
                    setConsumerFilters((prev) => ({
                      ...prev,
                      divisionId: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Divisions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Divisions</SelectItem>
                    {masterData.divisions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zone</Label>
                <Select
                  value={consumerFilters.zoneId}
                  onValueChange={(value) =>
                    setConsumerFilters((prev) => ({ ...prev, zoneId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Zones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Zones</SelectItem>
                    {masterData.zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>
                        {z.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Consumer Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectAllConsumers}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Consumer Name</TableHead>
                  <TableHead>Register Number</TableHead>
                  <TableHead>Contact Number</TableHead>
                  <TableHead>Division</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Services</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConsumers.map((consumer) => (
                  <Fragment key={consumer.id}>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={selectedConsumers.includes(consumer.id)}
                          onCheckedChange={() => toggleConsumer(consumer.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {consumer.fullName}
                      </TableCell>
                      <TableCell>{consumer.registrationNumber}</TableCell>
                      <TableCell>{consumer.contactNumber}</TableCell>
                      <TableCell>{consumer.division?.name || "-"}</TableCell>
                      <TableCell>{consumer.zone?.name || "-"}</TableCell>
                      <TableCell>
                        <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs">
                          {consumer.category?.name || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="custom"
                          size="sm"
                          onClick={() => handleViewCustomerServices(consumer)}
                          // className="hover:bg-transparent"
                        >
                          {expandedConsumerId === consumer.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Service Details Row */}
                    {expandedConsumerId === consumer.id && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={8} className="p-0">
                          <div className="p-4 border-t border-slate-200">
                            {/* <div className="mb-2">
                              <h4 className="text-sm font-semibold">{selectedConsumerName}'s Services</h4>
                            </div> */}

                            {serviceLoading ? (
                              <div className="flex justify-center items-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                              </div>
                            ) : customerServices.length > 0 ? (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-[60px]">
                                        Sl.No
                                      </TableHead>
                                      <TableHead>Service Name</TableHead>
                                      <TableHead className="text-right">
                                        Paid Amount
                                      </TableHead>
                                      <TableHead className="text-right">
                                        Due Amount
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {customerServices.map((service, index) => (
                                      <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                          {service.serviceName}
                                        </TableCell>
                                        <TableCell className="text-green-600 text-right">
                                          {service.paidAmount}
                                        </TableCell>
                                        <TableCell className="text-red-600 text-right">
                                          {service.dueAmount}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow>
                                      <TableCell
                                        colSpan={2}
                                        className="text-right font-medium"
                                      >
                                        Total Due Amount:
                                      </TableCell>
                                      <TableCell
                                        colSpan={2}
                                        className="text-right font-bold text-red-600"
                                      >
                                        {customerServices.reduce(
                                          (total, service) =>
                                            total + service.dueAmount,
                                          0
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </div>
                            ) : (
                              <div className="py-4 text-center text-muted-foreground">
                                No services found for this customer
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>

            {selectedConsumers.length > 0 && (
              <div className="mt-4 p-4 bg-purple-50 rounded-md">
                <p className="text-sm text-purple-700">
                  {selectedConsumers.length} consumer
                  {selectedConsumers.length !== 1 ? "s" : ""} selected
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Collection List Tab */}
      {activeTab === "collections" && (
        <Card>
          <CardContent className="pt-6">
            {/* Collection Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="space-y-2">
                <Label
                  htmlFor="collection-search"
                  className="flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Search
                </Label>
                <Input
                  id="collection-search"
                  placeholder="Search Collection..."
                  value={collectionSearchTerm}
                  onChange={(e) => setCollectionSearchTerm(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Service</Label>
                <Select
                  value={collectionFilters.serviceId}
                  onValueChange={(value) =>
                    setCollectionFilters((prev) => ({
                      ...prev,
                      serviceId: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={collectionFilters.status}
                  onValueChange={(value) =>
                    setCollectionFilters((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Collection Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entry No</TableHead>
                  <TableHead>Collection Name</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collections.map((collection) => (
                  <TableRow key={collection.id}>
                    <TableCell>{collection.entryNo}</TableCell>
                    <TableCell className="font-medium">
                      {collection.name}
                    </TableCell>
                    <TableCell>{collection.service}</TableCell>
                    <TableCell className="text-green-600 font-medium">
                      {collection.amount}
                    </TableCell>
                    <TableCell>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        {collection.billingCycle
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </TableCell>
                    <TableCell>{collection.period}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            collection.status === "active"
                              ? "bg-blue-600"
                              : "bg-gray-400"
                          }`}
                        ></div>
                        {collection.status === "active" ? "Active" : "Inactive"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewCollection(collection)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCollection(collection)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCollection(collection.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Collection Form Dialog */}
      <Dialog open={showCollectionForm} onOpenChange={setShowCollectionForm}>
        <DialogContent className="w-[720px] max-w-[95vw] h-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex justify-between items-center flex-row">
            <div className="w-[300px]">
              <DialogTitle>
                {isEditMode
                  ? "Edit Collection Configuration"
                  : "Create New Collection"}
              </DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? "Modify the collection configuration details"
                  : "Configure a new collection setup"}
              </DialogDescription>
            </div>
            <div className="flex items-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-sm text-purple-700">
                <span className="opacity-80">Entry Number:</span>
                <span className="font-semibold">
                  {entryNoLoading
                    ? "Loading..."
                    : collectionFormData.entryNo || "--"}
                </span>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={handleCollectionSubmit}>
            <div className="space-y-6 py-4">
              {/* Entry Number banner */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Collection Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={collectionFormData.name}
                    onChange={(e) =>
                      setCollectionFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter collection name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service">
                    Service <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={collectionFormData.serviceId}
                    onValueChange={handleServiceChange}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">
                    Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="amount"
                    value={collectionFormData.amount}
                    onChange={(e) =>
                      setCollectionFormData((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    placeholder="Enter collection amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billingCycle">
                    Billing Cycle <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={collectionFormData.billingCycle}
                    onValueChange={(value) =>
                      setCollectionFormData((prev) => ({
                        ...prev,
                        billingCycle: value,
                      }))
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select billing cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="one_time">One-time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {collectionFormData.billingCycle &&
                  collectionFormData.billingCycle !== "daily" &&
                  collectionFormData.billingCycle !== "one_time" && (
                    <div className="space-y-2">
                      <Label htmlFor="billingDate">
                        Billing Date <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={collectionFormData.billingDate}
                        onValueChange={(value) =>
                          setCollectionFormData((prev) => ({
                            ...prev,
                            billingDate: value,
                          }))
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select billing date" />
                        </SelectTrigger>
                        <SelectContent>
                          {getBillingDateOptions(
                            collectionFormData.billingCycle
                          )?.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                <div className="space-y-2">
                  <Label htmlFor="startDate">
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="startDate"
                      type="date"
                      value={collectionFormData.startDate}
                      onChange={(e) =>
                        setCollectionFormData((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">
                    End Date <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="endDate"
                      type="date"
                      value={collectionFormData.endDate}
                      onChange={(e) =>
                        setCollectionFormData((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      min={collectionFormData.startDate}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dueDays">Due Days</Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="dueDays"
                      type="number"
                      value={collectionFormData.dueDays.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!/e|E|\+|-/.test(value)) {
                          let num = parseInt(value) || 0;
                          const max =
                            collectionFormData.billingCycle === "daily"
                              ? 1
                              : collectionFormData.billingCycle === "weekly"
                              ? 7
                              : collectionFormData.billingCycle === "monthly"
                              ? 31
                              : collectionFormData.billingCycle === "quarterly"
                              ? 90
                              : collectionFormData.billingCycle === "yearly"
                              ? 365
                              : 365;
                          if (num > max) num = max;
                          setCollectionFormData((prev) => ({
                            ...prev,
                            dueDays: num,
                          }));
                        }
                      }}
                      min={1}
                      placeholder="Number of grace period days"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {collectionFormData.billingCycle === "daily"
                      ? "Maximum 1 day"
                      : collectionFormData.billingCycle === "weekly"
                      ? "Maximum 7 days"
                      : collectionFormData.billingCycle === "monthly"
                      ? "Maximum 31 days"
                      : collectionFormData.billingCycle === "quarterly"
                      ? "Maximum 90 days"
                      : "Maximum 365 days"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={collectionFormData.status}
                    onValueChange={(value) =>
                      setCollectionFormData((prev) => ({
                        ...prev,
                        status: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Selected consumers list removed from create form UI as per requirement */}
            </div>
            <DialogFooter className="flex items-center justify-between">
              {/* Left side (Selected Consumers) */}
              <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-sm text-purple-700">
                <span className="opacity-80">Selected Consumers:</span>
                <span className="font-semibold">
                  {selectedConsumers.length}
                </span>
              </div>

              {/* Right side (Cancel / Submit buttons) */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setShowCollectionForm(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || entryNoLoading}>
                  {isEditMode ? "Update Collection" : "Create Collection"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Collection Details Dialog */}
      <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Collection Details</DialogTitle>
            <DialogDescription>
              View complete information about this collection configuration
            </DialogDescription>
          </DialogHeader>
          {selectedCollectionDetails && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Entry Number
                  </Label>
                  <p className="text-sm">{selectedCollectionDetails.entryNo}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Name
                  </Label>
                  <p className="text-sm font-medium">
                    {selectedCollectionDetails.name}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Category
                  </Label>
                  <p className="text-sm">
                    {selectedCollectionDetails.category}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Service
                  </Label>
                  <p className="text-sm">{selectedCollectionDetails.service}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Amount
                  </Label>
                  <p className="text-sm font-medium">
                    {selectedCollectionDetails.amount}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Billing Cycle
                  </Label>
                  <p className="text-sm capitalize">
                    {selectedCollectionDetails.billingCycle}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Period
                  </Label>
                  <p className="text-sm">{selectedCollectionDetails.period}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Status
                  </Label>
                  <span
                    className={`inline-block px-2 py-1 text-xs rounded-full ${
                      selectedCollectionDetails.status === "active"
                        ? "bg-green-100 text-green-800"
                        : selectedCollectionDetails.status === "inactive"
                        ? "bg-gray-100 text-gray-800"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {selectedCollectionDetails.status}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will permanently delete the collection configuration.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteCollection}
              disabled={loading}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Collection Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View Collection Details</DialogTitle>
            <DialogDescription>
              Collection configuration and consumer information
            </DialogDescription>
          </DialogHeader>

          {collectionLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            selectedCollection && (
              <div className="space-y-6">
                {/* Collection Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Entry Number
                    </Label>
                    <p className="text-sm font-medium">
                      {selectedCollection.entryNumber}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Collection Name
                    </Label>
                    <p className="text-sm font-medium">
                      {selectedCollection.name}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Service
                    </Label>
                    <p className="text-sm">
                      {selectedCollection.service?.name}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Amount
                    </Label>
                    <p className="text-sm font-medium text-green-600">
                      ₹{selectedCollection.amount}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Billing Cycle
                    </Label>
                    <p className="text-sm capitalize">
                      {selectedCollection.billingCycle
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </p>
                  </div>
                  {selectedCollection.billingCycle !== "daily" &&
                    selectedCollection.billingCycle !== "one_time" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground">
                          Billing Date
                        </Label>
                        <p className="text-sm">
                          {formatBillingDate(
                            selectedCollection.billingCycle,
                            selectedCollection.billingDate
                          )}
                        </p>
                      </div>
                    )}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Status
                    </Label>
                    <p className="text-sm capitalize">
                      {selectedCollection.status}
                    </p>
                  </div>
                </div>

                {/* Consumers List */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold">
                    Consumers ({selectedCollection.consumers?.length || 0})
                  </h4>
                  {selectedCollection.consumers &&
                  selectedCollection.consumers.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Entry No</TableHead>
                            <TableHead>Registration No</TableHead>
                            <TableHead>Consumer Name</TableHead>
                            <TableHead>Contact Number</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedCollection.consumers.map(
                            (consumer: any, index: number) => (
                              <TableRow key={consumer.id}>
                                <TableCell>{consumer.entryNumber}</TableCell>
                                <TableCell>
                                  {consumer.registrationNumber}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {consumer.fullName}
                                </TableCell>
                                <TableCell>{consumer.contactNumber}</TableCell>
                                <TableCell>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs ${
                                      consumer.status === "active"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {consumer.status}
                                  </span>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No consumers found
                    </p>
                  )}
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Collection Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Edit Collection</span>
              {selectedCollection && (
                <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-sm text-purple-700">
                  <span className="opacity-80">Entry Number:</span>
                  <span className="font-semibold">
                    {selectedCollection.entryNumber}
                  </span>
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              Modify the collection configuration details
            </DialogDescription>
          </DialogHeader>

          {collectionLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            selectedCollection && (
              <div className="space-y-6">
                {/* Collection Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Collection Name</Label>
                    <Input
                      id="edit-name"
                      value={selectedCollection.name}
                      onChange={(e) =>
                        setSelectedCollection((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-service">Service</Label>
                    <Select
                      value={selectedCollection.serviceId || ""}
                      onValueChange={(value) => {
                        const service = services.find((s) => s.id === value);
                        setSelectedCollection((prev) => ({
                          ...prev,
                          serviceId: value,
                          service: {
                            ...prev.service,
                            id: value,
                            name: service?.name || "",
                          },
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selectedCollection.service?.name || "Select service"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-amount">Amount</Label>
                    <Input
                      id="edit-amount"
                      type="number"
                      value={selectedCollection.amount}
                      onChange={(e) =>
                        setSelectedCollection((prev) => ({
                          ...prev,
                          amount: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-billing-cycle">Billing Cycle</Label>
                    <Select
                      value={selectedCollection.billingCycle}
                      onValueChange={(value) =>
                        setSelectedCollection((prev) => ({
                          ...prev,
                          billingCycle: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="one_time">One Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedCollection.billingCycle &&
                    selectedCollection.billingCycle !== "daily" &&
                    selectedCollection.billingCycle !== "one_time" && (
                      <div className="space-y-2">
                        <Label htmlFor="edit-billing-date">Billing Date</Label>
                        <Select
                          value={
                            selectedCollection.billingDate?.toString() || ""
                          }
                          onValueChange={(value) =>
                            setSelectedCollection((prev) => ({
                              ...prev,
                              billingDate: value,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select billing date" />
                          </SelectTrigger>
                          <SelectContent>
                            {getBillingDateOptions(
                              selectedCollection.billingCycle
                            )?.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      value={selectedCollection.status}
                      onValueChange={(value) =>
                        setSelectedCollection((prev) => ({
                          ...prev,
                          status: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Consumer Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold">Select Consumers</h4>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="select-all-edit"
                        checked={selectAllEditConsumers}
                        onCheckedChange={toggleSelectAllEditConsumers}
                      />
                      <Label htmlFor="select-all-edit" className="text-sm">
                        Select All ({editConsumers.length})
                      </Label>
                    </div>
                  </div>

                  {editConsumers.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto shadow-sm">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead className="w-12 bg-slate-50"></TableHead>
                            <TableHead className="bg-slate-50">
                              Consumer Name
                            </TableHead>
                            <TableHead className="bg-slate-50">
                              Registration No
                            </TableHead>
                            <TableHead className="bg-slate-50">
                              Phone Number
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {editConsumers.map((consumer: any) => (
                            <TableRow
                              key={consumer.id}
                              className="hover:bg-slate-50"
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedEditConsumers.includes(
                                    consumer.id
                                  )}
                                  onCheckedChange={() =>
                                    toggleEditConsumer(consumer.id)
                                  }
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {consumer.fullName}
                              </TableCell>
                              <TableCell>
                                {consumer.registrationNumber}
                              </TableCell>
                              <TableCell>{consumer.contactNumber}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4 border rounded-lg bg-slate-50">
                      No consumers available
                    </p>
                  )}

                  {selectedEditConsumers.length > 0 && (
                    <div className="p-3 bg-purple-50 rounded-md border border-purple-100">
                      <p className="text-sm text-purple-700 flex items-center">
                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-purple-600 text-white text-xs mr-2 p-2">
                          {selectedEditConsumers.length}
                        </span>
                        consumer{selectedEditConsumers.length !== 1 ? "s" : ""}{" "}
                        selected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          <DialogFooter className="border-t pt-4 mt-2">
            <div className="flex justify-between w-full">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="border-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditCollection}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionManagement;
